import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, Check, FileDown, Plus, Rocket, Square } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { setupChecklist } from '@/lib/onboarding/setupState';
import { useSetupStatus } from '@/lib/onboarding/useSetupStatus';

/**
 * /getting-started — «Продолжить настройку».
 *
 * AK-584: страница была сиротой (вход из сайдбара удалён в AK-381) и теперь
 * возвращается как экран, куда ведёт баннер «Настройка не закончена» и куда
 * попадает тот, кто пропустил мастер первого входа. Мастер и этот чек-лист
 * показывают ОДНИ И ТЕ ЖЕ шаги — состояние считается общей функцией
 * `setupChecklist`, чтобы они не разъехались.
 *
 * У пункта «Поля» действия дублируют развилку мастера (Р3): нарисовать на
 * карте или загрузить файл — равноправно.
 */
export function GettingStartedPage() {
  const navigate = useNavigate();
  const { counts, isLoading, isError } = useSetupStatus();

  if (isLoading) {
    return (
      <AppShell>
        <div className="text-muted-foreground">Загружаем…</div>
      </AppShell>
    );
  }

  // Здесь, в отличие от баннера, молчать нельзя: страница целиком про
  // состояние настройки. Не удалось узнать — так и говорим, а не печатаем
  // «ни одного хозяйства» на живом клиенте.
  if (isError || !counts) {
    return (
      <AppShell>
        <div className="max-w-3xl">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Rocket className="h-6 w-6 text-primary" />
            Продолжить настройку
          </h1>
          <Alert variant="destructive" className="mt-6" data-testid="setup-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Не удалось узнать, что уже настроено. Обновите страницу — если
              повторится, напишите в поддержку.
            </AlertDescription>
          </Alert>
        </div>
      </AppShell>
    );
  }

  const items = setupChecklist({
    farms: counts.farms,
    fields: counts.fields,
    total_area_ha: counts.totalAreaHa,
  });

  return (
    <AppShell>
      <div className="max-w-3xl">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Rocket className="h-6 w-6 text-primary" />
          Продолжить настройку
        </h1>
        <p className="text-muted-foreground">
          Три шага до работающей системы. Порядок свободный, но без полей
          остальное показывать нечего.
        </p>

        <Card className="mt-6 overflow-hidden">
          <ul role="list" className="divide-y divide-border">
            {items.map((item) => (
              <li
                key={item.key}
                data-testid={`setup-item-${item.key}`}
                className="flex flex-wrap items-center gap-3 p-4"
              >
                <span
                  aria-hidden
                  className={
                    item.done
                      ? 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'
                      : 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'
                  }
                >
                  {item.done ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Square className="h-3 w-3" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground">
                    {item.title}
                    <span className="sr-only">
                      {item.done ? ' — готово' : ' — не сделано'}
                    </span>
                  </div>
                  <div className="mt-0.5 text-sm text-muted-foreground">
                    {item.description}
                  </div>
                </div>

                {item.key === 'fields' && !item.done ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="accent-brand"
                      size="sm"
                      data-testid="setup-draw-field"
                      onClick={() =>
                        navigate({ to: '/map', search: { addField: 1 } })
                      }
                    >
                      <Plus className="h-4 w-4" />
                      Нарисовать на карте
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="setup-import-fields"
                      onClick={() =>
                        navigate({
                          to: '/map',
                          search: { addField: 1, import: 1 },
                        })
                      }
                    >
                      <FileDown className="h-4 w-4" />
                      Загрузить файл
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate({ to: item.path })}
                  >
                    Открыть
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </AppShell>
  );
}

export default GettingStartedPage;
